import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AuthContext = createContext(null);

// Dedupes concurrent silent-refresh attempts across every in-flight request.
let refreshPromise = null;
function refreshAccessToken() {
  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${API}/auth/refresh`, {}, { withCredentials: true })
      .then(({ data }) => {
        localStorage.setItem('access_token', data.access_token);
        return data.access_token;
      })
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

// Set synchronously (during AuthProvider's render, not inside a useEffect) so it is
// always assigned before any request — including the very first checkAuth() call on
// a hard page reload — can possibly be dispatched.
let onSessionExpired = () => {};

// Silent session refresh: the 8-hour access token used to expire "silently" — any
// request after that would just fail with 401 and leave pages blank/broken until the
// user did a manual hard refresh. Registered once at module import time (NOT inside a
// React effect) so it is guaranteed to be attached before ANY request — including the
// very first GET /auth/me fired by checkAuth() on mount/hard-reload — can be dispatched.
// On any 401, we transparently exchange the httponly refresh_token cookie (valid 7
// days) for a new access token and retry the original request. Only if the refresh
// itself fails (refresh token also expired / logged out elsewhere) do we log the user
// out — which redirects to /login via ProtectedRoute, no hard browser refresh needed.
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isAuthCall = originalRequest?.url?.includes('/auth/login') || originalRequest?.url?.includes('/auth/refresh');
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry && !isAuthCall) {
      originalRequest._retry = true;
      try {
        const newToken = await refreshAccessToken();
        originalRequest.headers = { ...originalRequest.headers, Authorization: `Bearer ${newToken}` };
        return axios(originalRequest);
      } catch {
        localStorage.removeItem('access_token');
        onSessionExpired();
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = not auth
  const [loading, setLoading] = useState(true);
  onSessionExpired = () => setUser(false);

  const checkAuth = useCallback(async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) { setUser(false); setLoading(false); return; }
      const { data } = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(data);
    } catch {
      setUser(false);
      localStorage.removeItem('access_token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    if (data.access_token) localStorage.setItem('access_token', data.access_token);
    setUser(data);
    return data;
  };

  const logout = async () => {
    const token = localStorage.getItem('access_token');
    try {
      await axios.post(`${API}/auth/logout`, {}, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    } catch { /* ignore */ }
    localStorage.removeItem('access_token');
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
